/**
 * Client autocomplete — search by company name (reads the hierarchy). Picking a result stores the client
 * id; the input shows the company name. Controlled by `clientId` + `companyName`.
 */

import { useEffect, useRef, useState } from 'react';
import { hierarchy } from '../lib/api';

interface Match { id: string; companyName: string }

export function ClientPicker(
  { clientId, companyName, onChange }: { clientId: string; companyName: string; onChange: (id: string, name: string) => void },
): JSX.Element {
  const [term, setTerm] = useState(companyName);
  const [results, setResults] = useState<Match[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => { setTerm(companyName); }, [companyName]);

  const onType = (v: string) => {
    setTerm(v);
    onChange('', v); // typing clears the selection until a result is picked
    setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (v.trim().length < 1) { setResults([]); return; }
      try {
        const p = await hierarchy.clients({ search: v.trim(), pageSize: 8 });
        setResults(p.rows.map((r) => ({ id: r.id, companyName: r.companyName })));
      } catch { setResults([]); }
    }, 250);
  };

  const pick = (m: Match) => { onChange(m.id, m.companyName); setTerm(m.companyName); setOpen(false); };

  return (
    <div className="field full client-picker">
      <label htmlFor="client">Client</label>
      <input
        id="client" type="text" value={term} autoComplete="off" placeholder="Search client company name…"
        onChange={(e) => onType(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {clientId
        ? <span className="hint">Selected client ID <code>{clientId}</code></span>
        : <span className="hint">Type a company name and choose from the list.</span>}
      {open && results.length > 0 && (
        <ul className="ac-list">
          {results.map((m) => (
            <li key={m.id} onMouseDown={() => pick(m)}>
              <span>{m.companyName}</span> <span className="muted">{m.id}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
